import { ROUTES } from '@/constants'

/**
 * Who can reach which admin screen.
 *
 * Three staff roles, each a subset of the one before it: ADMIN sees everything,
 * MANAGER runs the shop day to day but cannot reach store settings or accounts,
 * EMPLOYEE handles orders and nothing else. Mirrors `STAFF_ROLES`, `ORDER_ROLES`
 * and the bare `@Roles(Role.ADMIN)` guards on the backend — this file is the
 * convenience layer, `RolesGuard` is the enforcement.
 *
 * Kept in one module because `RequireAdmin` and `AdminLayout` must agree. A nav
 * that lists a screen the guard then refuses is worse than no nav entry, and
 * that is exactly what two copies of this list would eventually produce.
 */

export const STAFF_ROLES = ['ADMIN', 'MANAGER', 'EMPLOYEE']

/**
 * Who may open the packing dashboard at /packer. Mirrors `PACKING_ROLES` on
 * the backend. PACKER is deliberately *not* in `STAFF_ROLES`: a packer has no
 * admin area at all, only this one screen, and keeping the role out of the
 * staff list is what makes `RequireAdmin` refuse it without a special case.
 * Managers and admins are in so the owner can supervise the queue.
 */
export const PACKING_ROLES = ['ADMIN', 'MANAGER', 'PACKER']

/** Admin-only sections. Everything else under /admin is open to managers. */
export const ADMIN_ONLY_ROUTES = [ROUTES.adminSettings, ROUTES.adminCustomers]

/**
 * The only sections an employee gets. An allowlist rather than a deny list
 * like the manager's: a new admin screen should be closed to employees until
 * someone decides otherwise, not open because nobody thought to list it.
 */
export const EMPLOYEE_ROUTES = [ROUTES.adminDashboard, ROUTES.adminOrders]

export const isStaffRole = (role) => STAFF_ROLES.includes(role)

export const isPackerRole = (role) => role === 'PACKER'

export const canAccessPackingArea = (role) => PACKING_ROLES.includes(role)

/**
 * Runs the shop rather than only working its orders — the frontend twin of
 * the backend's `isManagement`. Gates what is *fetched* (the alerts feed
 * 403s for an employee), not what is routed; routing is `canAccessAdminRoute`.
 */
export const isManagementRole = (role) => role === 'ADMIN' || role === 'MANAGER'

/**
 * `pathname` is `route` or a page beneath it (`/admin/orders/abc`). The `/`
 * boundary keeps `/admin/settings-export` from matching `/admin/settings`.
 */
const isUnderRoute = (pathname, route) =>
  pathname === route || pathname.startsWith(`${route}/`)

/**
 * Whether `role` may open `pathname`.
 *
 * Prefix-matched so a future `/admin/settings/tax` inherits the restriction
 * rather than silently opening up.
 *
 * The dashboard is the admin root, so for the employee it is matched exactly:
 * a prefix match on `/admin` would let every admin page through.
 */
export function canAccessAdminRoute(pathname, role) {
  if (!isStaffRole(role)) return false
  if (role === 'ADMIN') return true
  if (role === 'EMPLOYEE') {
    return EMPLOYEE_ROUTES.some((route) =>
      route === ROUTES.adminDashboard ? pathname === route : isUnderRoute(pathname, route)
    )
  }
  return !ADMIN_ONLY_ROUTES.some((route) => isUnderRoute(pathname, route))
}

/** Human label for a role, for badges and selects. */
export const ROLE_LABELS = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  EMPLOYEE: 'Employee',
  PACKER: 'Packer',
  CUSTOMER: 'Customer',
}
